import React, { useEffect, useState, useMemo } from "react";
import { GetDietPlan, normalHeader } from "../../../utils/Api";
import moment from "moment";
import DynamicPopupModal from "../../../components/NoDataModal";
import DatePicker from "react-date-picker";

import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import SliderComponent from "../SliderComponent";

export default function Diet() {
  const params = useMemo(() => {
    const query = new URLSearchParams(window.location.search);
    return {
      UserType: query.get("UserType"),
      AuthToken: query.get("AuthToken")?.replace(/\s/g, "+"), // Regex fix for spaces
      UserId: parseInt(query.get("UserId")),
      isPlan: parseInt(query.get("isPlan") || 0),
    };
  }, []);

  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));

  useEffect(() => {
    // 1. Initialize guard variables
    let didCancel = false;
    const controller = new AbortController();

    if (params.isPlan === 0) {
      setShowModal(true);
      return; // Exit early if no plan
    }

    const fetchDiet = async () => {
      setIsLoading(true);
      const header = {
        ...normalHeader,
        AuthToken: params.AuthToken,
        userId: params.UserId,
      };

      try {
        // Pass the controller signal to your API utility if it supports fetch/axios signals
        const res = await GetDietPlan(
          params.UserId,
          params.UserType,
          date + "%2000:00:00",
          header,
          { signal: controller.signal },
        );

        // 2. Only update state if the component hasn't re-rendered or unmounted
        if (!didCancel) {
          if (res.status && res.data) {
            const timeSlots = [
              "06:00",
              "09:00",
              "12:00",
              "13:00",
              "15:00",
              "18:00",
              "21:00",
              "23:00",
            ];

            const groupedDiets = timeSlots
              .map((slot) =>
                res.data.filter((d) => {
                  const formattedTime =
                    d.time.length === 4 ? `0${d.time}` : d.time;
                  return formattedTime === slot;
                }),
              )
              .filter((group) => group.length > 0);

            setData([
              {
                rxDate: res.data[0]?.dateTime || date,
                diets: groupedDiets,
              },
            ]);
          } else {
            setData([]);
          }
        }
      } catch (err) {
        if (err.name === "AbortError") {
          console.log("Fetch aborted: Multiple clicks or StrictMode");
        } else if (!didCancel) {
          console.error("API Error:", err);
        }
      } finally {
        if (!didCancel) setIsLoading(false);
      }
    };

    fetchDiet();

    // 3. Cleanup function: This kills the double-hit
    return () => {
      didCancel = true;
      controller.abort();
    };
  }, [date, params]); // params is memoized, so it won't trigger re-fetch unless URL actually changes

  return (
    <div className="flex flex-col h-screen max-w-md mx-auto text-thtext bg-blue-light overflow-hidden">
      <DynamicPopupModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        type={params.isPlan === 1 ? "no-data" : "no-plan"}
      />

      <div className="p-4 z-10 sticky top-0">
        <div className="flex items-center px-2 bg-white rounded-md p-2 shadow-sm border border-gray-100">
          <h2 className="w-32 text-sm font-semibold text-gray-600">
            Select date:
          </h2>
          <DatePicker
            format="dd/MM/yyyy"
            maxDate={moment().add(1, "week").toDate()}
            minDate={moment().subtract(1, "week").toDate()}
            clearIcon={null}
            value={moment(date).toDate()}
            onChange={(e) => e && setDate(moment(e).format("YYYY-MM-DD"))}
            className="w-full diet-datepicker"
          />
        </div>
      </div>
      <div className="p-4">
        <SliderComponent type={"diet"} userId={params.UserId} />
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-10 scrollbar-hide">
        {isLoading && data.length === 0 ? (
          <div className="w-full py-20 text-center text-white opacity-50">
            Loading plan...
          </div>
        ) : data.length > 0 ? (
          data.map((item, index) => (
            <DietCardView key={index} data={item} date={date} />
          ))
        ) : (
          <div className="w-full py-20 text-center text-white opacity-50">
            No data found
          </div>
        )}
      </div>
    </div>
  );
}

const DietCardView = ({ data, date }) => {
  const getFormattedHeading = (targetDate) => {
    const today = moment().startOf("day");
    const d = moment(targetDate, "DD/MM/YYYY");

    if (d.isSame(today, "day")) return `Today ${d.format("DD-MMM-YYYY")}`;
    if (d.isSame(moment(today).subtract(1, "day"), "day"))
      return `Yesterday ${d.format("DD-MMM-YYYY")}`;
    return d.format("DD-MMM-YYYY");
  };

  const formatDisplayTime = (rawTime) => {
    return moment(rawTime, "HH:mm").format("hh:mm a");
  };

  return (
    <div className="my-2">
      <h3 className="text-white text-lg font-semibold mb-3">
        Diet plan for {getFormattedHeading(data.rxDate)}
      </h3>

      <div className="w-full flex flex-col bg-white rounded-xl shadow-md overflow-hidden">
        {data.diets.map((group, i) => {
          const isOdd = i % 2 !== 0;

          return (
            <div
              key={i}
              className="flex min-h-[10rem] h-auto border-b border-gray-50 last:border-none"
            >
              {/* Content Left or Image Left based on index */}
              {!isOdd ? (
                <DietRowContent
                  group={group}
                  align="right"
                  formatDisplayTime={formatDisplayTime}
                />
              ) : (
                <DietRowImages group={group} />
              )}

              {/* Timeline Center */}
              <div className="w-[15%] flex flex-col items-center">
                <div className="w-0.5 h-full bg-gray-200 relative">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-orange-500 rounded-full border-2 border-white shadow-sm"></div>
                </div>
              </div>

              {!isOdd ? (
                <DietRowImages group={group} />
              ) : (
                <DietRowContent
                  group={group}
                  align="left"
                  formatDisplayTime={formatDisplayTime}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Sub-component for the text info
const DietRowContent = ({ group, align, formatDisplayTime }) => (
  <div
    className={`w-[42.5%] p-3 flex flex-col justify-center ${align === "right" ? "items-end text-right" : "items-start text-left"}`}
  >
    <span className="text-orange-600 font-bold text-xs uppercase tracking-wider mb-1">
      {formatDisplayTime(group[0].time)}
    </span>
    <div className="space-y-1">
      {group.map((item, idx) => (
        <p
          key={idx}
          className="text-gray-800 text-sm leading-tight break-words font-medium"
        >
          {item.diet}
        </p>
      ))}
    </div>
  </div>
);

// Sub-component for the images info
const DietRowImages = ({ group }) => (
  <div className="w-[42.5%] p-3 flex items-center justify-center">
    <div
      className={`grid gap-1 w-20 h-20 ${group.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}
    >
      {group.map((item, idx) => (
        <img
          key={idx}
          src={item.URL}
          alt="diet"
          className="w-full h-full object-cover rounded shadow-sm bg-gray-100"
          onError={(e) => {
            e.target.src =
              "https://theme-assets.getbento.com/sensei/4cf5794.sensei/assets/images/catering-item-placeholder-704x520.png";
          }}
        />
      ))}
    </div>
  </div>
);
